import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { Ray } from '@babylonjs/core/Culling/ray';

class ThirdPersonCameraController {
  game: any;
  camera: ArcRotateCamera | null;
  attachPoint: TransformNode | null;
  _defaultAlpha: number;
  _defaultBeta: number;
  _defaultRadius: number;
  _minRadius: number;
  _maxRadius: number;
  _maxBeta: number;
  _wheelPrecision: number;
  _cameraCollisionEnabled: boolean;
  _collisionOffset: number;
  _aimMode: boolean;
  _aimRadius: number;
  _aimBetaOffset: number;
  _recoilPitch: number;
  _recoilYaw: number;
  _recoilDecay: number;

  constructor(game: any) {
    this.game = game;
    this.camera = null;
    this.attachPoint = null;
    this._defaultAlpha = -1.5;
    this._defaultBeta = 1.2;
    this._defaultRadius = 5.0;
    this._minRadius = 2.0;
    this._maxRadius = 15.0;
    this._maxBeta = Math.PI / 2 + 0.2;
    this._wheelPrecision = 200;
    this._cameraCollisionEnabled = true;
    this._collisionOffset = 0.3;
    this._aimMode = false;
    this._aimRadius = 2.0;
    this._aimBetaOffset = -0.15;
    this._recoilPitch = 0;
    this._recoilYaw = 0;
    this._recoilDecay = 8.0;
  }

  init(scene: Scene, targetMesh: any) {
    const attachPoint = new TransformNode('cameraAttachPoint', scene);
    attachPoint.parent = targetMesh;
    attachPoint.position = new Vector3(0, 1.5, 0);
    this.attachPoint = attachPoint;

    const camera = new ArcRotateCamera(
      'thirdPersonCamera',
      this._defaultAlpha,
      this._defaultBeta,
      this._defaultRadius,
      Vector3.Zero(),
      scene
    );
    camera.attachControl(true);
    camera.setTarget(attachPoint);
    camera.wheelPrecision = this._wheelPrecision;
    camera.lowerRadiusLimit = this._minRadius;
    camera.upperRadiusLimit = this._maxRadius;
    camera.lowerBetaLimit = 0.2;
    camera.upperBetaLimit = this._maxBeta;
    camera.checkCollisions = true;
    camera.collisionRadius = new Vector3(0.3, 0.3, 0.3);
    camera.panningSensibility = 0;
    camera.inputs.removeByType('ArcRotateCameraKeyboardMoveInput');

    this.camera = camera;
    scene.activeCamera = camera;
  }

  getForwardDirection(): Vector3 {
    if (!this.camera) return Vector3.Forward();
    const direction = this.camera.getForwardRay().direction;
    return new Vector3(direction.x, 0, direction.z).normalize();
  }

  getAimRay(): Ray {
    if (!this.camera) return new Ray(Vector3.Zero(), Vector3.Forward(), 100);
    const origin = this.camera.position;
    const forward = this.camera.getForwardRay().direction;
    return new Ray(origin, forward, 100);
  }

  setAimMode(enabled: boolean) {
    this._aimMode = enabled;
  }

  applyRecoil(pitchAmount: number, yawAmount: number) {
    this._recoilPitch += pitchAmount;
    this._recoilYaw += yawAmount;
  }

  update(delta: number) {
    if (!this.camera || !this.attachPoint) return;

    if (this._aimMode) {
      const targetRadius = this._aimRadius;
      this.camera.radius += (targetRadius - this.camera.radius) * 5.0 * delta;

      const targetBeta = this._defaultBeta + this._aimBetaOffset;
      this.camera.beta += (targetBeta - this.camera.beta) * 5.0 * delta;
    }

    if (Math.abs(this._recoilPitch) > 0.001 || Math.abs(this._recoilYaw) > 0.001) {
      this.camera.beta += this._recoilPitch * delta;
      this.camera.alpha += this._recoilYaw * delta;

      this._recoilPitch *= Math.max(0, 1 - this._recoilDecay * delta);
      this._recoilYaw *= Math.max(0, 1 - this._recoilDecay * delta);
    } else {
      this._recoilPitch = 0;
      this._recoilYaw = 0;
    }

    if (this._cameraCollisionEnabled && this.camera.radius > this._minRadius) {
      this._handleCameraCollision();
    }
  }

  _handleCameraCollision() {
    if (!this.camera || !this.attachPoint) return;
    const scene = this.camera.getScene();
    if (!scene) return;

    const targetPos = this.attachPoint.getAbsolutePosition();
    if (!targetPos) return;

    const camPos = this.camera.position;
    const dir = camPos.subtract(targetPos);
    const distance = dir.length();
    if (distance < 0.001) return;

    dir.normalize();
    const ray = new Ray(targetPos, dir, distance);
    const hit = scene.pickWithRay(ray, (mesh: any) => {
      return mesh.isPickable && mesh.checkCollisions;
    });

    if (hit && hit.hit && hit.distance < distance) {
      const safeDist = Math.max(hit.distance - this._collisionOffset, this._minRadius);
      this.camera.radius = safeDist;
    }
  }

  dispose() {
    if (this.camera) {
      this.camera.dispose();
    }
    if (this.attachPoint) {
      this.attachPoint.dispose();
    }
    this.camera = null;
    this.attachPoint = null;
  }
}

export { ThirdPersonCameraController };
